/**
 * Authentication hook.
 *
 * Custom hook to access the authentication context. Provides user data,
 * authentication status, and authentication methods (login, register, logout).
 * Must be used within an AuthProvider component.
 *
 * @module useAuth
 */

import { useContext } from 'react';
import { AuthContext } from '@/contexts/AuthContext';

/**
 * Hook to access authentication context.
 *
 * Provides access to the current user, authentication status, and authentication
 * methods (login, register, logout, verifyToken). Must be used within an
 * AuthProvider component.
 *
 * @returns Authentication context object containing:
 *   - `user`: Current user object or null if not authenticated
 *   - `isAuthenticated`: Boolean indicating authentication status
 *   - `isLoading`: Boolean indicating if authentication check is in progress
 *   - `login`: Function to log in a user
 *   - `register`: Function to register a new user
 *   - `logout`: Function to log out the current user
 *   - `verifyToken`: Function to verify the current authentication token
 *
 * @throws Error if used outside of AuthProvider component
 *
 * @example
 * ```typescript
 * const { user, isAuthenticated, login } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <SignIn onLogin={login} />;
 * }
 *
 * return <Dashboard user={user} />;
 * ```
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

